<script setup>
/* eslint-disable */
import { computed, watch, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CONVERSATION_SIDEBAR_SECTIONS } from './conversationSidebarSections';

import AccordionItem from 'dashboard/components-next/panel/PanelAccordion.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ClientProductsPanel from './contact/ClientProductsPanel.vue';
import ContactNotes from './contact/ContactNotes.vue';
import LeadOriginPanel from './contact/LeadOriginPanel.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import ContactWahaInfo from './contact/ContactWahaInfo.vue';
import Draggable from 'vuedraggable';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import MacrosList from './Macros/List.vue';
import KanbanActions from './KanbanActions.vue';
import LeadScorePanel from 'dashboard/components/widgets/leadScoring/LeadScorePanel.vue';
import Jornada360Compact from 'dashboard/components/widgets/jornada360/Jornada360Compact.vue';
import QuickActionButtons from 'dashboard/components/widgets/QuickActionButtons.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import ShopifyOrdersList from '../../../components/widgets/conversation/ShopifyOrdersList.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import QuickCreateTaskDialog from 'dashboard/routes/dashboard/tasks/QuickCreateTaskDialog.vue';
import TasksAPI from 'dashboard/api/tasks';
import { useConfig } from 'dashboard/composables/useConfig';
import { useKanbanBoardEnabled } from 'dashboard/composables/useKanbanBoardEnabled';
import { useToggle } from '@vueuse/core';
import { useRouter, useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanAPI from '../../../api/kanban';
import FunnelAPI from '../../../api/funnel';
import { emitter } from 'shared/helpers/mitt';
/* global axios */
// SiteUp: usa o axios GLOBAL (window.axios = createAxios(axios)), que carrega os
// headers de auth — o `import axios from 'axios'` cru NÃO tem auth e dava 401 nas
// chamadas de ai_auto_create / ai_update do Kanban.

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  uiSettings,
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);

// SiteUp: personalizar seções do painel da conversa (ocultar/exibir) pra reduzir
// poluição visual. Persistido por usuário em uiSettings.conversation_sidebar_hidden_items.
const isCustomizingSidebar = ref(false);
// Fonte única das seções: compartilhada com a tela do Modo Foco.
const conversationSidebarSections = CONVERSATION_SIDEBAR_SECTIONS;

const hiddenSidebarItems = computed(() => {
  const hidden = uiSettings.value?.conversation_sidebar_hidden_items;
  return Array.isArray(hidden) ? hidden : [];
});
const isSidebarItemHidden = name => hiddenSidebarItems.value.includes(name);
const setSidebarItemVisible = (name, visible) => {
  const set = new Set(hiddenSidebarItems.value);
  if (visible) set.delete(name);
  else set.add(name);
  updateUISettings({ conversation_sidebar_hidden_items: Array.from(set) });
};

const { t } = useI18n();
const sectionLabelFor = name => {
  const section = conversationSidebarSections.find(s => s.name === name);
  if (!section) return name;
  return section.label || t(section.labelKey);
};

// SiteUp V2 (2026-08-10, pedido do Lucas): o cabeçalho "Contatos" ficou só com
// título + fechar. "Agendar ação" desceu pro card do contato, junto de
// editar/excluir, que é a linha das ações sobre essa pessoa.
//
// ATENÇÃO — a engrenagem "Personalizar seções" saiu daqui a pedido do Lucas
// ("dá pra tirar por enquanto, vamos ter uma configuração global"), e com ela
// saiu o ÚNICO gatilho de `isCustomizingSidebar`. O modo de personalização
// continua implementado e sem entrada de UI até a config global existir; é
// dívida conhecida, não código esquecido.
const contactPanelHeaderButtons = computed(() => []);
const onContactPanelHeaderAction = () => {};

// O botão só aparece se houver ao menos UMA ação no popover: agendar mensagem
// depende da feature da conta; criar tarefa depende da conversa carregada.
// Sem isso o gatilho abriria um menu vazio.
const canScheduleAction = computed(
  () => isScheduleMessageFeatureEnabled || Boolean(conversationId.value)
);

// Popover de agendar: posição ancorada no rect do botão que o disparou.
const actionChooserStyle = ref({});
const POPOVER_WIDTH = 256;
const openActionChooser = triggerRect => {
  if (!triggerRect) {
    showActionChooser.value = !showActionChooser.value;
    return;
  }
  const margin = 12;
  const left = Math.max(
    margin,
    Math.min(
      triggerRect.left,
      window.innerWidth - POPOVER_WIDTH - margin
    )
  );
  actionChooserStyle.value = {
    top: `${triggerRect.bottom + 6}px`,
    left: `${left}px`,
  };
  showActionChooser.value = !showActionChooser.value;
};
const conversationSidebarItems = ref([]);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();


const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const isLinearFeatureEnabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.LINEAR
);

const isKanbanFeatureEnabled = useKanbanBoardEnabled();

// Lead Scoring — feature extraída do VOIP. Só renderiza aba/accordion quando
// a flag está habilitada na conta. Backend também gate no cron noturno.
const isLeadScoringFeatureEnabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.LEAD_SCORING
);

const isScheduleMessageFeatureEnabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.SCHEDULE_MESSAGES
);

// Get Stacklab configuration
const { isStacklab } = useConfig();

const [showScheduleMessageModal, toggleScheduleModal] = useToggle(false);
const showActionChooser = ref(false);
const showQuickCreateTask = ref(false);

const openCreateTask = () => {
  showQuickCreateTask.value = true;
};
const onTaskCreated = () => {
  showQuickCreateTask.value = false;
};
const store = useStore();
const router = useRouter();
const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');

const linkedTasks = ref([]);
const isLoadingLinkedTasks = ref(false);
const fetchLinkedTasks = async () => {
  const contactId = currentChat.value?.meta?.sender?.id;
  if (!contactId) { linkedTasks.value = []; return; }
  isLoadingLinkedTasks.value = true;
  try {
    const { data } = await TasksAPI.list({ contact_id: contactId, status_key: '' });
    linkedTasks.value = (Array.isArray(data) ? data : data.tasks || []).slice(0, 20);
  } catch (e) { linkedTasks.value = []; }
  finally { isLoadingLinkedTasks.value = false; }
};
const openLinkedTask = (id) => { if (!id) return; router.push({ name: 'tasks', query: { open: String(id) } }); };
const priorityDotColor = (p) => ({ none: '#9ba3af', low: '#657389', medium: '#378fff', high: '#f5950a', urgent: '#ef4444' }[p] || '#9ba3af');
const statusBadgeColor = (k) => ({ pending: '#eab308', in_progress: '#378fff', done: '#10b981', cancelled: '#9ba3af' }[k] || '#9ba3af');
const statusBadgeLabel = (k) => ({ pending: 'Pendente', in_progress: 'Em andamento', done: 'Concluída', cancelled: 'Cancelada' }[k] || 'Pendente');
watch(
  () => currentChat.value?.meta?.sender?.id,
  (newId) => { if (newId) fetchLinkedTasks(); else linkedTasks.value = []; },
  { immediate: true }
);


// Kanban + Funnels (needed before AI controls)
const kanbanItemComputed = computed(() => {
  if (
    !currentChat.value?.kanban_items ||
    currentChat.value.kanban_items.length === 0
  )
    return null;
  return currentChat.value.kanban_items[0];
});

const funnels = computed(() =>
  store.getters['funnel/getFunnels']?.filter(funnel => funnel.active) || []
);

const currentFunnel = computed(() => {
  const funnelId = kanbanItemComputed.value?.funnel_id;
  if (!funnelId) return null;
  return funnels.value.find(f => f.id == funnelId);
});

// --- IA Pause/Resume via custom_attributes ---
const aiPauseLoading = ref(false);
const autoCreateLoading = ref(false);

// SiteUp: a auto-criação por IA agora é NATIVA (endpoint próprio, sem n8n). O botão
// só deve aparecer quando existe um funil com ai_auto_create.enabled = true.
const canAutoCreate = computed(() =>
  funnels.value.some(f => f.settings?.ai_auto_create?.enabled)
);

const inboxHasAgentBot = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return false;
  const agentBotId = store.state.agentBots.agentBotInbox[Number(inboxId)];
  return !!agentBotId;
});

const isAiAgentFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, 'ai_agent')
);

const inboxHasAiAgentBot = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  if (!inboxId) return false;
  const agentBotId = store.state.agentBots.agentBotInbox[Number(inboxId)];
  if (!agentBotId) return false;
  const bot = store.state.agentBots.records.find(
    b => b.id === Number(agentBotId)
  );
  return !!bot?.bot_config?.ai_config;
});

const showIaToggle = computed(
  () => isAiAgentFeatureEnabled.value && inboxHasAiAgentBot.value
);

const aiPaused = computed(() => {
  return currentChat.value?.custom_attributes?.ai_paused === true;
});

const toggleAiPause = async () => {
  aiPauseLoading.value = true;
  try {
    const newValue = !aiPaused.value;
    const convId = Number(currentChat.value?.id || conversationId.value);
    await store.dispatch('updateCustomAttributes', {
      conversationId: convId,
      customAttributes: { ai_paused: newValue },
    });
    emitter.emit('newToastMessage', {
      message: newValue ? 'IA pausada nesta conversa' : 'IA reativada nesta conversa',
      type: 'success',
    });
  } catch {
    emitter.emit('newToastMessage', { message: 'Erro ao alterar status da IA', type: 'error' });
  } finally {
    aiPauseLoading.value = false;
  }
};

const autoCreateKanbanItem = async () => {
  // Usa o funil com auto-criação por IA habilitada (sem fallback — o botão só
  // aparece quando existe um). Chama o endpoint NATIVO (gemini-3-flash), sem n8n.
  const funnel = funnels.value.find(f => f.settings?.ai_auto_create?.enabled);
  if (!funnel) {
    emitter.emit('newToastMessage', {
      message: 'Nenhum funil com auto-criação por IA ativada.',
      type: 'error',
    });
    return;
  }
  autoCreateLoading.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/kanban_items/ai_auto_create`,
      {
        conversation_id: String(conversationId.value),
        funnel_id: funnel.id,
      }
    );
    if (data?.success) {
      emitter.emit('newToastMessage', {
        message: `Card criado: ${data.title || 'OK'}`,
        type: 'success',
      });
      emitter.emit('kanban.item.updated');
      hydrateKanbanItemsIfMissing();
    } else {
      emitter.emit('newToastMessage', {
        message: data?.error || 'Erro ao criar card no Kanban',
        type: 'error',
      });
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: error?.response?.data?.error || 'Erro ao criar card no Kanban',
      type: 'error',
    });
  } finally {
    autoCreateLoading.value = false;
  }
};

// SiteUp: "Atualizar com IA" — re-roda a extração da conversa no card que já existe
// (refresca os campos da IA preservando agente/checklist/notas).
const updateKanbanItemWithAi = async () => {
  const item = kanbanItemComputed.value;
  if (!item) return;
  autoCreateLoading.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/kanban_items/${item.id}/ai_update`
    );
    if (data?.success) {
      emitter.emit('newToastMessage', {
        message: `Card atualizado: ${data.title || 'OK'}`,
        type: 'success',
      });
      if (data.item && currentChat.value?.kanban_items?.[0]) {
        Object.assign(currentChat.value.kanban_items[0], data.item);
      }
      if (currentChat.value?.id) {
        store.dispatch('updateConversationKanbanItems', {
          conversationId: currentChat.value.id,
          kanbanItems: currentChat.value.kanban_items,
        });
      }
      emitter.emit('kanban.item.updated');
    } else {
      emitter.emit('newToastMessage', {
        message: data?.error || 'Erro ao atualizar card com IA',
        type: 'error',
      });
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: error?.response?.data?.error || 'Erro ao atualizar card com IA',
      type: 'error',
    });
  } finally {
    autoCreateLoading.value = false;
  }
};

// Modal de Status
const showStatusModal = ref(false);
const statusForm = ref({
  status: '',
  reason: '',
  selectedOffer: null,
  selectedWinReason: null,
  selectedLossReason: null,
});

// Modal de confirmação pra deletar o item de kanban da conversa
const showDeleteKanbanModal = ref(false);
const isDeletingKanban = ref(false);

const openDeleteKanbanModal = () => {
  if (!kanbanItemComputed.value) return;
  showDeleteKanbanModal.value = true;
};

const confirmDeleteKanbanItem = async () => {
  if (!kanbanItemComputed.value || isDeletingKanban.value) return;
  isDeletingKanban.value = true;
  try {
    const deletedId = kanbanItemComputed.value.id;
    await KanbanAPI.deleteItem(deletedId);
    emitter.emit('newToastMessage', {
      message: 'Item do Kanban deletado',
      type: 'success',
    });
    emitter.emit('kanban.item.updated');
    emitter.emit('kanban.item.deleted', { id: deletedId });
    // Atualiza chat.kanban_items no store removendo o item deletado
    const chat = currentChat.value;
    if (chat?.id) {
      const remaining = (chat.kanban_items || []).filter(i => i.id !== deletedId);
      store.dispatch('updateConversationKanbanItems', { conversationId: chat.id, kanbanItems: remaining });
    }
    showDeleteKanbanModal.value = false;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao deletar item do Kanban',
      type: 'error',
    });
  } finally {
    isDeletingKanban.value = false;
  }
};

// Computed para motivos de ganho
const winReasons = computed(() => {
  return currentFunnel.value?.settings?.win_reasons || [];
});

// Computed para motivos de perda
const lossReasons = computed(() => {
  return currentFunnel.value?.settings?.loss_reasons || [];
});

// Computed para obter as offers disponíveis do item
const availableOffers = computed(() => {
  const offers = kanbanItemComputed.value?.item_details?.offers || [];
  return Array.isArray(offers) ? offers : [];
});

const handleStatusClick = async () => {
  if (!kanbanItemComputed.value) return;
  
  // Buscar funnels antes de abrir o modal
  try {
    await store.dispatch('funnel/fetch');
  } catch (error) {
    console.error('Erro ao buscar funnels:', error);
  }

  // Inicializa o form com o status atual
  const currentStatus = kanbanItemComputed.value.item_details?.status || currentChat.value?.status || 'open';
  
  statusForm.value = {
    status: currentStatus,
    reason: kanbanItemComputed.value.item_details?.reason || '',
    selectedOffer: kanbanItemComputed.value.item_details?.closed_offer || null,
    selectedWinReason: kanbanItemComputed.value.item_details?.win_reason || null,
    selectedLossReason: kanbanItemComputed.value.item_details?.loss_reason || null,
  };

  showStatusModal.value = true;
};

const handleStatusSave = async () => {
  if (!kanbanItemComputed.value) return;
  
  try {
    // Preparar o campo reason baseado no status
    let reasonText = null;
    
    if (statusForm.value.status === 'won') {
      // Se selecionou "Outro", usar o texto do textarea
      if (statusForm.value.selectedWinReason === 'other') {
        reasonText = statusForm.value.reason || null;
      } else {
        // Usar o título do win_reason selecionado
        const selectedReason = winReasons.value.find(
          r => r.id === statusForm.value.selectedWinReason
        );
        reasonText = selectedReason?.title || null;
      }
    } else if (statusForm.value.status === 'lost') {
      // Se selecionou "Outro", usar o texto do textarea
      if (statusForm.value.selectedLossReason === 'other') {
        reasonText = statusForm.value.reason || null;
      } else {
        // Usar o título do loss_reason selecionado
        const selectedReason = lossReasons.value.find(
          r => r.id === statusForm.value.selectedLossReason
        );
        reasonText = selectedReason?.title || null;
      }
    }
    
    // Sempre fazer update completo do item_details para garantir que o status seja atualizado
    const updatedItem = {
      ...kanbanItemComputed.value,
      item_details: {
        ...kanbanItemComputed.value.item_details,
        status: statusForm.value.status,
        reason: reasonText,
        closed_offer: statusForm.value.status === 'won' ? statusForm.value.selectedOffer : null,
        win_reason: statusForm.value.status === 'won' && statusForm.value.selectedWinReason !== 'other' ? statusForm.value.selectedWinReason : null,
        loss_reason: statusForm.value.status === 'lost' && statusForm.value.selectedLossReason !== 'other' ? statusForm.value.selectedLossReason : null,
      },
    };

    const { data } = await KanbanAPI.update(kanbanItemComputed.value.id, updatedItem);
    if (data) {
      // Atualizar o item no currentChat
      if (currentChat.value?.kanban_items?.[0]) {
        Object.assign(currentChat.value.kanban_items[0], data);
      }
    }

    showStatusModal.value = false;
    
    // Emitir eventos para atualizar
    emitter.emit('kanban.item.updated');
    emitter.emit('conversation.updated', currentChat.value?.id);

    // Mostrar toast de sucesso
    emitter.emit('newToastMessage', {
      message: 'Status atualizado com sucesso',
      type: 'success',
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao atualizar status',
      type: 'error',
    });
  }
};
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);
// SiteUp (Int-F4): gate do bloco "WhatsApp" (dados WAHA) — mesmo critério
// de canal que Waha::InboxGate.waha_backed? usa no backend (Channel::Api).
const isAWahaChannel = computed(() => channelType.value === 'Channel::Api');

const contactGetter = useMapGetter('contacts/getContact');
const contactConversationsGetter = useMapGetter(
  'contactConversations/getContactConversation'
);
const contactNotesGetter = useMapGetter('contactNotes/getAllNotesByContactId');

const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

// SiteUp (CT-C): a seção "Lead" só existe quando a conversa veio de anúncio —
// sem ctwa_clid não houve referral e a seção seria uma casca vazia.
const isFromAd = computed(() =>
  Boolean(
    currentChat.value?.custom_attributes?.ctwa_clid ||
      contactAdditionalAttributes.value?.origin_channel
  )
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

// SiteUp V2 (2026-08-11): contagem no título dos acordeões.
//
// A primeira versão pedia o número ao próprio módulo, por emit. Não funcionava:
// o PanelAccordion renderiza o conteúdo com `v-if="isOpen"`, então uma seção
// FECHADA nunca monta o filho e nunca emite — o selo só aparecia depois de
// abrir, que é exatamente o que ele deveria poupar. Medido no staging: nenhum
// dos nove acordeões mostrava número.
//
// Agora o número sai do store, aqui, e por isso o painel também dispara as duas
// buscas: sem elas o contador de uma seção fechada seria sempre zero — um
// número honesto e inútil.
const previousConversationsCount = computed(() => {
  const all = contactConversationsGetter.value(contactId.value) || [];
  // Mesmo filtro do ContactConversations: a conversa aberta não é "anterior".
  return all.filter(c => c.id !== Number(conversationId.value)).length || null;
});

const contactNotesCount = computed(
  () => (contactNotesGetter.value(contactId.value) || []).length || null
);

const contactAttributesCount = computed(
  () => Object.keys(contact.value?.custom_attributes || {}).length || null
);

const fetchSectionCounts = () => {
  if (!contactId.value) return;
  store.dispatch('contactConversations/get', contactId.value);
  store.dispatch('contactNotes/get', { contactId: contactId.value });
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
    fetchSectionCounts();
  }
});

onMounted(fetchSectionCounts);

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

// Phase 2.9.8 K*.3: quando uma Quick Action e executada, recarrega dados
// do Kanban + conversa pra refletir mudancas (mover stage, valor, etc).
const onQuickActionExecuted = () => {
  // Re-fetch do contato pra pegar labels/custom_attributes novos + kanban
  getContactDetails();
  emitter.emit('refresh-kanban-panel');
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
  });
};

// Busca/filtros retornam conversas sem `kanban_items` no payload (diferente do index).
// Hidrata o objeto da conversa selecionada para o painel e o KanbanActions funcionarem.
const hydrateKanbanItemsIfMissing = async () => {
  const id = conversationId.value;
  if (!id) return;

  const chat = currentChat.value;
  if (!chat?.id || String(chat.id) !== String(id)) return;
  if (chat.kanban_items?.length) return;

  try {
    const { data } = await KanbanAPI.getItemsByConversation(id);
    const items = data?.items ?? [];
    if (!items.length) return;

    const sorted = [...items].sort(
      (a, b) =>
        new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
    );

    const latest = currentChat.value;
    if (
      latest?.id === chat.id &&
      String(latest.id) === String(id) &&
      !latest.kanban_items?.length
    ) {
      latest.kanban_items = sorted;
    }
  } catch {
    // ignorar — conversa pode não ter item de kanban
  }
};

// SiteUp: "Criar manualmente" — abre um seletor de funil/etapa (sempre pergunta) e
// cria o card no funil escolhido pelo corretor. Usa o KanbanAPI autenticado.
const manualCreateLoading = ref(false);
const manualPickerOpen = ref(false);
const manualFunnelId = ref(null);
const manualStage = ref('');
const manualFunnelObj = computed(() =>
  funnels.value.find(f => f.id == manualFunnelId.value)
);
const manualStages = computed(() => {
  const stages = manualFunnelObj.value?.stages || {};
  return Object.entries(stages)
    .map(([key, s]) => ({ key, name: s?.name || key, position: s?.position ?? 0 }))
    .sort((a, b) => a.position - b.position);
});
const openManualPicker = () => {
  const first = funnels.value[0];
  if (!first) {
    emitter.emit('newToastMessage', {
      message: 'Nenhum funil disponível para criar o card.',
      type: 'error',
    });
    return;
  }
  manualFunnelId.value = first.id;
  manualStage.value = manualStages.value[0]?.key || 'lead';
  manualPickerOpen.value = true;
};
watch(manualFunnelId, () => {
  manualStage.value = manualStages.value[0]?.key || 'lead';
});
const createManualKanbanItem = async () => {
  const funnel = manualFunnelObj.value || funnels.value[0];
  if (!funnel) {
    emitter.emit('newToastMessage', {
      message: 'Nenhum funil disponível para criar o card.',
      type: 'error',
    });
    return;
  }
  manualCreateLoading.value = true;
  try {
    const stage =
      manualStage.value || (funnel.stages ? Object.keys(funnel.stages)[0] : 'lead');
    const { data } = await KanbanAPI.createItem({
      funnel_id: funnel.id,
      funnel_stage: stage,
      position: 0,
      conversation_display_id: String(conversationId.value),
      item_details: {
        title: currentChat.value?.meta?.sender?.name || 'Novo card',
        description: '',
        conversation_id: conversationId.value,
      },
    });
    if (data && currentChat.value) {
      currentChat.value.kanban_items = [data];
    }
    manualPickerOpen.value = false;
    emitter.emit('kanban.item.updated');
    emitter.emit('newToastMessage', { message: 'Card criado no Kanban', type: 'success' });
  } catch {
    emitter.emit('newToastMessage', { message: 'Erro ao criar card', type: 'error' });
  } finally {
    manualCreateLoading.value = false;
  }
};

watch(
  () => [conversationId.value, currentChat.value?.id, currentChat.value?.kanban_items],
  () => {
    hydrateKanbanItemsIfMissing();
  },
  { immediate: true }
);

onMounted(() => {
  conversationSidebarItems.value = [...conversationSidebarItemsOrder.value];

  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
  // Load funnels for status modal
  store.dispatch('funnel/fetch');
  // Check if inbox has an AI agent bot
  const inboxId = currentChat.value?.inbox_id;
  if (inboxId) {
    store.dispatch('agentBots/fetchAgentBotInbox', inboxId);
  }
  // Load full agent bots list so inboxHasAiAgentBot can inspect bot_config.ai_config
  if (!store.state.agentBots.records.length) {
    store.dispatch('agentBots/get');
  }
});

// Keep local draggable list in sync when computed order changes (feature flags /
// first paint before account id). Avoid clobbering mid-drag.
watch(
  conversationSidebarItemsOrder,
  nextOrder => {
    if (dragging.value) return;
    const nextNames = nextOrder.map(item => item.name).join('|');
    const currentNames = conversationSidebarItems.value
      .map(item => item.name)
      .join('|');
    if (nextNames !== currentNames) {
      conversationSidebarItems.value = [...nextOrder];
    }
  },
  { immediate: true }
);

// Re-fetch agent bot when conversation/inbox changes
watch(() => currentChat.value?.inbox_id, (newInboxId) => {
  if (newInboxId) {
    store.dispatch('agentBots/fetchAgentBotInbox', newInboxId);
  }
});
</script>

<template>
  <div class="w-full relative" data-testid="focus-spot-sidebar">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      :buttons="contactPanelHeaderButtons"
      @click="onContactPanelHeaderAction"
      @close="closeContactPanel"
    />

    <!-- Agendar ação: popover ancorado no BOTÃO, que agora vive no card do
         contato (junto de editar/excluir). Posição vem do rect do gatilho, não
         de um top fixo — o card muda de altura conforme avatar/nome. -->
    <div
      v-if="showActionChooser"
      v-on-click-outside="() => showActionChooser = false"
      class="fixed z-50 w-64 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-lg"
      :style="actionChooserStyle"
    >
        <button
          v-if="isScheduleMessageFeatureEnabled"
          class="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-n-alpha-1 text-left transition-colors"
          @click="showActionChooser = false; toggleScheduleModal()"
        >
          <span class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-blue-500/10">
            <span class="i-ri-mail-line size-4 text-blue-600 dark:text-blue-400" />
          </span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-n-slate-12">Agendar mensagem</div>
            <div class="text-xs text-n-slate-11">Envia automaticamente em data/hora futura</div>
          </div>
        </button>
        <button
          class="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-n-alpha-1 text-left transition-colors border-t border-n-weak"
          @click="showActionChooser = false; openCreateTask()"
        >
          <span class="flex size-8 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
            <span class="i-ri-list-check size-4 text-emerald-600 dark:text-emerald-400" />
          </span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-n-slate-12">Criar tarefa</div>
            <div class="text-xs text-n-slate-11">Adiciona um lembrete vinculado a esta conversa</div>
          </div>
        </button>
    </div>

    <!-- IA toggle (gated por ai_agent + bot na inbox). Auto-Criar Kanban virou icone na secao Kanban. -->
    <div v-if="showIaToggle" class="px-4 pb-3">
      <button
        class="ai-toggle-btn flex w-full items-center justify-center gap-2 rounded-xl px-3 h-9 text-xs font-semibold transition-all duration-200"
        :class="aiPaused ? 'ai-toggle-btn--paused' : 'ai-toggle-btn--active'"
        :disabled="aiPauseLoading"
        @click="toggleAiPause"
      >
        <span v-if="aiPauseLoading" class="i-ri-loader-2-line size-4 animate-spin" />
        <template v-else>
          <span class="relative flex size-4 items-center justify-center">
            <span
              v-if="!aiPaused"
              class="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-50"
            />
            <span
              class="relative inline-flex size-2.5 rounded-full"
              :class="aiPaused ? 'bg-red-500' : 'bg-green-500'"
            />
          </span>
          {{ aiPaused ? 'IA Pausada' : 'IA Ativa' }}
          <span
            :class="aiPaused ? 'i-ri-play-line' : 'i-ri-pause-line'"
            class="ml-auto size-3.5 opacity-60"
          />
        </template>
      </button>
    </div>

    <!-- SITEUP V2 / G2: padding do cpanel (mockup 03 ~16px).
         mb-3.5 = o MESMO gap-3.5 que separa os blocos da lista abaixo. Sem ele
         o card do contato encostava no primeiro bloco (medido no staging:
         0px de gap contra 14px entre todos os outros), e como os dois usam
         bg-n-solid-1 os cards se fundiam num bloco só. -->
    <div class="mb-3.5 px-3">
      <ContactInfo
        :contact="contact"
        :channel-type="channelType"
        :can-schedule="canScheduleAction"
        @schedule="openActionChooser"
      />
    </div>

    <!-- SiteUp: aba "Produtos do Cliente" (cursos/livros/eventos) — só na conta habilitada (24). -->
    <ClientProductsPanel
      v-if="Number(currentAccountId) === 24 && contact?.id"
      :contact-id="contact.id"
      :conversation-id="currentChat?.id"
    />

    <div class="list-group px-3 pb-8">
      <!-- SiteUp: personalizar seções — arrasta p/ reordenar + liga/desliga -->
      <div v-if="isCustomizingSidebar" class="px-4 pb-3">
        <p class="mb-1.5 text-xs text-n-slate-11">
          Arraste pra reordenar; desligue pra ocultar.
        </p>
        <Draggable
          :list="conversationSidebarItems"
          item-key="name"
          handle=".cp-drag"
          animation="200"
          ghost-class="ghost"
          class="flex flex-col gap-0.5"
          @end="onDragEnd"
        >
          <template #item="{ element }">
            <div
              class="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-n-alpha-2"
            >
              <span
                class="cp-drag i-ri-draggable size-4 flex-shrink-0 cursor-move text-n-slate-10"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                sectionLabelFor(element.name)
              }}</span>
              <ToggleSwitch
                :model-value="!isSidebarItemHidden(element.name)"
                @update:model-value="
                  value => setSidebarItemVisible(element.name, value)
                "
              />
            </div>
          </template>
        </Draggable>
        <!-- SiteUp: botão Concluir também no fim da lista — em listas longas o
             usuário não precisa rolar de volta ao topo pra sair do modo edição. -->
        <button
          type="button"
          class="flex items-center justify-center gap-1.5 w-full mt-3 px-3 py-2 rounded-xl text-sm font-medium text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
          @click="isCustomizingSidebar = false"
        >
          <span class="i-ri-check-line size-4" />
          Concluir
        </button>
      </div>
      <Draggable
        v-else
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3.5"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div v-show="!isSidebarItemHidden(element.name)">
            <div
              v-if="element.name === 'conversation_actions'"
              class="conversation--actions"
            >
            <AccordionItem
              icon="i-ri-equalizer-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              icon="i-ri-group-line"
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              icon="i-ri-information-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'waha_info' && isAWahaChannel && contact?.id"
          >
            <AccordionItem
              icon="i-ri-whatsapp-line"
              title="WhatsApp"
              :is-open="isContactSidebarItemOpen('is_waha_info_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_waha_info_open', value)"
            >
              <ContactWahaInfo :contact="contact" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'lead_origin' && isFromAd">
            <AccordionItem
              icon="i-ri-megaphone-line"
              :title="$t('LEAD_ORIGIN.TITLE')"
              :is-open="isContactSidebarItemOpen('is_lead_origin_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_lead_origin_open', value)"
            >
              <LeadOriginPanel
                :conversation-attributes="currentChat.custom_attributes || {}"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              icon="i-ri-price-tag-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :count="contactAttributesCount"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'lead_scoring' &&
              isLeadScoringFeatureEnabled &&
              contact && contact.id
            "
          >
            <AccordionItem
              icon="i-ri-fire-line"
              title="Score de Lead"
              :is-open="isContactSidebarItemOpen('is_lead_scoring_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_lead_scoring_open', value)"
            >
              <LeadScorePanel :contact-id="contact.id" :contact="contact" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              icon="i-ri-chat-3-line"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :count="previousConversationsCount"
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              icon="i-ri-flashlight-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              icon="i-ri-list-check"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              icon="i-ri-shopping-bag-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              icon="i-ri-sticky-note-line"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :count="contactNotesCount"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'kanban_panel' && isKanbanFeatureEnabled"
          >
            <AccordionItem
              icon="i-ri-layout-grid-line"
              :title="$t('SIDEBAR.KANBAN_BOARD_TITLE', {
                title: store.getters['kanban/getKanbanTitle'],
              })"
              :is-open="isContactSidebarItemOpen('is_kanban_panel_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_kanban_panel_open', value)
              "
            >
              <template #button>
                <button
                  v-if="!kanbanItemComputed && canAutoCreate"
                  class="mr-2 p-1 text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
                  title="Auto-criar card com IA"
                  :disabled="autoCreateLoading"
                  @click.stop="autoCreateKanbanItem"
                >
                  <span
                    class="size-4 inline-block"
                    :class="autoCreateLoading ? 'i-ri-loader-2-line animate-spin' : 'i-ri-sparkling-line'"
                  />
                </button>
                <button
                  v-if="kanbanItemComputed && canAutoCreate"
                  class="mr-2 p-1 text-n-brand hover:bg-n-brand/10 rounded-md transition-colors"
                  title="Atualizar card com IA (relê a conversa)"
                  :disabled="autoCreateLoading"
                  @click.stop="updateKanbanItemWithAi"
                >
                  <span
                    class="size-4 inline-block"
                    :class="autoCreateLoading ? 'i-ri-loader-2-line animate-spin' : 'i-ri-refresh-line'"
                  />
                </button>
<!-- SiteUp V2 (2026-08-11): o texto "Mudar Status" quebrava em duas
                     linhas no painel estreito (print do Lucas). Vira ícone; a
                     mesma função vive na pill de status do card completo. -->
                <button
                  v-if="kanbanItemComputed"
                  class="mr-2 p-1 text-n-slate-11 hover:bg-n-alpha-2 rounded-md transition-colors"
                  :title="$t('KANBAN.CHANGE_STATUS')"
                  @click.stop="handleStatusClick"
                >
                  <span class="i-ri-price-tag-3-line size-4 inline-block" />
                </button>
                <button
                  v-if="kanbanItemComputed"
                  class="mr-2 p-1 text-n-ruby-11 hover:bg-n-ruby-9/10 rounded-md transition-colors"
                  title="Deletar item do Kanban"
                  @click.stop="openDeleteKanbanModal"
                >
                  <span class="i-ri-delete-bin-line size-4 inline-block" />
                </button>
                              </template>
              <!-- Estado vazio (sem card) — estilo mockup -->
              <div
                v-if="!kanbanItemComputed"
                class="flex flex-col items-center gap-2 px-4 py-5 text-center"
              >
                <div class="flex size-12 items-center justify-center rounded-2xl bg-n-alpha-2">
                  <span class="i-ri-layout-grid-line size-6 text-n-slate-10" />
                </div>
                <p class="mt-1 text-sm font-medium text-n-slate-12">Sem card no Kanban</p>
                <p class="text-xs leading-relaxed text-n-slate-11">
                  Esta conversa ainda não está em nenhum funil.
                </p>
                <template v-if="!manualPickerOpen">
                  <button
                    v-if="canAutoCreate"
                    class="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-n-brand px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    :disabled="autoCreateLoading || manualCreateLoading"
                    @click="autoCreateKanbanItem"
                  >
                    <span
                      class="size-3.5"
                      :class="autoCreateLoading ? 'i-ri-loader-2-line animate-spin' : 'i-ri-sparkling-line'"
                    />
                    Criar card com IA
                  </button>
                  <button
                    class="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:opacity-60"
                    @click="openManualPicker"
                  >
                    <span class="size-3.5 i-ri-add-line" />
                    Criar manualmente
                  </button>
                  <p v-if="canAutoCreate" class="mt-1 text-[11px] leading-relaxed text-n-slate-10">
                    A IA lê a conversa e preenche etapa, valor e um resumo do lead.
                  </p>
                </template>
                <div v-else class="mt-1 flex w-full flex-col gap-2 text-left">
                  <div>
                    <label class="mb-1 block text-[11px] font-medium text-n-slate-11">Funil</label>
                    <select
                      v-model="manualFunnelId"
                      class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-2.5 py-2 text-xs text-n-slate-12 focus:border-n-brand focus:outline-none"
                    >
                      <option v-for="f in funnels" :key="f.id" :value="f.id">{{ f.name }}</option>
                    </select>
                  </div>
                  <div v-if="manualStages.length">
                    <label class="mb-1 block text-[11px] font-medium text-n-slate-11">Etapa</label>
                    <select
                      v-model="manualStage"
                      class="w-full rounded-xl border border-n-weak bg-n-alpha-1 px-2.5 py-2 text-xs text-n-slate-12 focus:border-n-brand focus:outline-none"
                    >
                      <option v-for="s in manualStages" :key="s.key" :value="s.key">{{ s.name }}</option>
                    </select>
                  </div>
                  <div class="mt-1 flex gap-2">
                    <button
                      class="flex-1 rounded-xl border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 disabled:opacity-60"
                      :disabled="manualCreateLoading"
                      @click="manualPickerOpen = false"
                    >
                      Cancelar
                    </button>
                    <button
                      class="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-n-brand px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      :disabled="manualCreateLoading"
                      @click="createManualKanbanItem"
                    >
                      <span v-if="manualCreateLoading" class="size-3.5 i-ri-loader-2-line animate-spin" />
                      {{ manualCreateLoading ? 'Criando…' : 'Criar card' }}
                    </button>
                  </div>
                </div>
              </div>
              <KanbanActions v-if="kanbanItemComputed" :conversation-id="conversationId" />
            </AccordionItem>
          </div>
            <div v-else-if="element.name === 'jornada_360' && contact?.id">
              <AccordionItem
                icon="i-ri-planet-line"
                :title="t('JORNADA_360.TITLE')"
                :is-open="isContactSidebarItemOpen('is_jornada_360_open')"
                compact
                @toggle="
                  value => toggleSidebarUIState('is_jornada_360_open', value)
                "
              >
                <Jornada360Compact
                  class="!mx-0 !mb-0 !border-0"
                  :contact="contact"
                  :show-header="false"
                />
              </AccordionItem>
            </div>
          <!-- SiteUp: Tarefas Vinculadas — mesmo padrão (AccordionItem) dos demais itens -->
          <div
            v-else-if="element.name === 'tasks_panel' && currentChat?.meta?.sender?.id"
          >
            <AccordionItem
              icon="i-ri-list-check"
              title="Tarefas"
              :count="linkedTasks.length || null"
              :is-open="!isContactSidebarItemOpen('is_tasks_panel_collapsed')"
              @toggle="value => toggleSidebarUIState('is_tasks_panel_collapsed', !value)"
            >
              <template #button>
                <button
                  type="button"
                  class="mr-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-n-brand transition-colors hover:bg-n-brand/10"
                  title="Criar tarefa nesta conversa"
                  @click="openCreateTask"
                >
                  <span class="i-ri-add-line size-3.5" />
                  Nova
                </button>
              </template>
              <div
                v-if="isLoadingLinkedTasks && !linkedTasks.length"
                class="py-2 text-center text-xs text-n-slate-11"
              >
                Carregando...
              </div>
              <div
                v-else-if="!linkedTasks.length"
                class="flex flex-col items-center gap-1 py-3 text-xs text-n-slate-11"
              >
                <span class="i-ri-inbox-line size-5 text-n-slate-9" />
                Nenhuma tarefa vinculada
              </div>
              <ul v-else class="flex flex-col gap-1">
                <li
                  v-for="t in linkedTasks.slice(0, 4)"
                  :key="t.id"
                  class="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-n-alpha-2"
                  @click="openLinkedTask(t.id)"
                >
                  <span
                    class="size-[7px] shrink-0 rounded-full"
                    :style="{ backgroundColor: priorityDotColor(t.priority) }"
                  />
                  <span
                    :class="[
                      'flex-1 truncate text-[13.5px] text-n-slate-12',
                      t.status_key === 'done' && 'line-through opacity-60',
                    ]"
                    >{{ t.title }}</span
                  >
                  <span
                    class="shrink-0 rounded-full bg-n-alpha-2 px-[11px] py-1 text-[12.5px] font-bold"
                    :style="{
                      color: statusBadgeColor(t.status_key),
                    }"
                    >{{ statusBadgeLabel(t.status_key) }}</span
                  >
                </li>
              </ul>
              <button
                v-if="linkedTasks.length > 4"
                type="button"
                class="mt-1.5 w-full rounded-md py-1.5 text-xs font-medium text-n-brand hover:bg-n-brand/10"
                @click="router.push({ name: 'tasks', query: { contact_id: currentChat.meta.sender.id } })"
              >
                Ver todas ({{ linkedTasks.length }})
              </button>
            </AccordionItem>
          </div>
          </div>
        </template>
      </Draggable>
    </div>

    <!-- Quick Create Task Dialog (v223) -->
    <QuickCreateTaskDialog
      v-if="showQuickCreateTask"
      :conversation-display-id="currentChat?.id || null"
      :conversation-assignee-id="currentChat?.meta?.assignee?.id || null"
      :contact-id="currentChat?.meta?.sender?.id || null"
      @close="showQuickCreateTask = false"
      @created="onTaskCreated"
    />

    <!-- Schedule Message Modal -->
    <ScheduleMessageModal
      v-if="showScheduleMessageModal"
      ref="scheduleMessageModal"
      :variation="outline"
      :conversation-id="conversationId"
      :inbox-id="currentChat.inbox_id"
      :show="showScheduleMessageModal"
      @close="toggleScheduleModal"
    />

    <!-- Modal de Status -->
    <Modal
      v-model:show="showStatusModal"
      :on-close="() => (showStatusModal = false)"
      :show-close-button="true"
      size="small"
    >
      <div
        class="p-4 bg-n-solid-1 rounded-md border border-n-weak shadow-lg"
      >
        <h3 class="text-lg font-extrabold mb-4">Atualizar status</h3>
        <div class="space-y-4">
          <div class="flex gap-2">
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-n-alpha-2"
              :class="
                statusForm.status === 'open'
                  ? 'border-n-strong bg-n-alpha-2 font-bold text-n-slate-12'
                  : 'border-n-weak'
              "
              @click="statusForm.status = 'open'"
            >
              <span class="truncate">Em aberto</span>
              <span
                v-if="statusForm.status === 'open'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-green-50 dark:hover:bg-green-900"
              :class="
                statusForm.status === 'won'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900 font-bold text-green-700 dark:text-green-100'
                  : 'border-n-weak'
              "
              @click="statusForm.status = 'won'"
            >
              <span class="truncate">Ganho</span>
              <span
                v-if="statusForm.status === 'won'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <button
              class="flex-1 px-3 py-1.5 border rounded-md text-xs font-normal transition-colors hover:bg-red-50 dark:hover:bg-red-900"
              :class="
                statusForm.status === 'lost'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900 font-bold text-red-700 dark:text-red-100'
                  : 'border-n-weak'
              "
              @click="statusForm.status = 'lost'"
            >
              <span class="truncate">Perdido</span>
              <span
                v-if="statusForm.status === 'lost'"
                class="ml-2 text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
          </div>
          <div v-if="statusForm.status === 'lost'" class="space-y-2">
            <div v-if="lossReasons.length > 0" class="space-y-2">
              <label class="text-sm font-medium">Motivo da perda</label>
              <select
                v-model="statusForm.selectedLossReason"
                class="w-full p-2 border border-n-weak rounded-md text-xs bg-n-solid-1 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              >
                <option :value="null">Selecione um motivo</option>
                <option
                  v-for="reason in lossReasons"
                  :key="reason.id"
                  :value="reason.id"
                >
                  {{ reason.title }}
                </option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div v-if="statusForm.selectedLossReason === 'other' || !lossReasons.length" class="space-y-2">
              <label class="text-sm font-medium">{{ lossReasons.length > 0 ? 'Descreva o motivo' : 'Observações adicionais' }}</label>
              <textarea
                v-model="statusForm.reason"
                class="w-full p-2 border border-n-weak rounded-md text-xs bg-n-solid-1 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                rows="3"
                :placeholder="lossReasons.length > 0 ? 'Descreva o motivo da perda' : 'Descreva detalhes adicionais (opcional)'"
              />
            </div>
          </div>
          <div v-if="statusForm.status === 'won'" class="space-y-2">
            <div v-if="winReasons.length > 0" class="space-y-2">
              <label class="text-sm font-medium">Motivo do ganho</label>
              <select
                v-model="statusForm.selectedWinReason"
                class="w-full p-2 border border-n-weak rounded-md text-xs bg-n-solid-1 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
              >
                <option :value="null">Selecione um motivo</option>
                <option
                  v-for="reason in winReasons"
                  :key="reason.id"
                  :value="reason.id"
                >
                  {{ reason.title }}
                </option>
                <option value="other">Outro</option>
              </select>
            </div>
            <div v-if="statusForm.selectedWinReason === 'other'" class="space-y-2">
              <label class="text-sm font-medium">Descreva o motivo</label>
              <textarea
                v-model="statusForm.reason"
                class="w-full p-2 border border-n-weak rounded-md text-xs bg-n-solid-1 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                rows="3"
                placeholder="Descreva o motivo do ganho"
              />
            </div>
          </div>
          <div v-if="statusForm.status === 'won' && availableOffers.length > 0" class="space-y-2">
            <label class="text-sm font-medium">Selecione a oferta de fechamento</label>
            <div class="space-y-2 max-h-40 overflow-y-auto">
              <button
                v-for="(offer, index) in availableOffers"
                :key="index"
                type="button"
                class="flex items-center w-full px-3 py-2 rounded-md transition-colors gap-3 border border-n-weak text-xs font-normal hover:bg-n-alpha-2"
                :class="[
                  statusForm.selectedOffer === offer
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30 font-bold text-green-700 dark:text-green-300'
                    : '',
                ]"
                @click="statusForm.selectedOffer = offer"
              >
                <div class="flex flex-col items-start flex-1 min-w-0">
                  <div class="flex items-center gap-2 w-full">
                    <span class="font-medium truncate">{{ offer.description || 'Oferta sem descrição' }}</span>
                    <span class="text-green-600 dark:text-green-400 font-semibold text-xs">
                      {{ new Intl.NumberFormat(offer.currency?.locale || 'pt-BR', { style: 'currency', currency: offer.currency?.code || 'BRL' }).format(offer.value || 0) }}
                    </span>
                  </div>
                </div>
                <span
                  v-if="statusForm.selectedOffer === offer"
                  class="text-green-500 text-xs font-semibold flex-shrink-0"
                  >Selecionada</span
                >
              </button>
              <div
                v-if="availableOffers.length === 0"
                class="px-3 py-2 text-n-slate-10 text-xs text-center"
              >
                Nenhuma oferta disponível
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="showStatusModal = false"
              >Cancelar</Button
            >
            <Button
              variant="solid"
              color="blue"
              size="sm"
              :disabled="
                (statusForm.status === 'won' && availableOffers.length > 0 && !statusForm.selectedOffer)
              "
              @click="handleStatusSave"
              >Salvar</Button
            >
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de confirmação pra deletar item do Kanban da conversa -->
    <Modal
      v-model:show="showDeleteKanbanModal"
      :on-close="() => (showDeleteKanbanModal = false)"
      :show-close-button="true"
      size="small"
    >
      <div
        class="p-5 bg-n-solid-1 rounded-md border border-n-weak shadow-lg"
      >
        <div class="flex items-start gap-3 mb-4">
          <div class="flex-shrink-0 size-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <span class="i-ri-delete-bin-line size-5 text-red-600 dark:text-red-400" />
          </div>
          <div class="flex-1">
            <h3 class="text-base font-semibold text-n-slate-12 mb-1">
              Deletar item do Kanban?
            </h3>
            <p class="text-sm text-n-slate-11">
              Essa ação remove o item permanentemente do funil. A conversa não é afetada.
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-5">
          <button
            class="px-4 py-2 rounded-md text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            :disabled="isDeletingKanban"
            @click="showDeleteKanbanModal = false"
          >
            Não
          </button>
          <button
            class="px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            :disabled="isDeletingKanban"
            @click="confirmDeleteKanbanItem"
          >
            <span v-if="isDeletingKanban" class="i-ri-loader-4-line size-4 animate-spin inline-block mr-1" />
            {{ isDeletingKanban ? 'Deletando...' : 'Sim, deletar' }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
/* SITEUP V2 / G2 — chrome do painel; cards vêm do PanelAccordion/PanelCard */
:deep(.contact--profile) {
  @apply pb-0;
}

.ai-toggle-btn {
  border: 2px solid transparent;
}

.ai-toggle-btn--active {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.25);
  color: #16a34a;

  &:hover {
    background: rgba(34, 197, 94, 0.14);
  }
}

.ai-toggle-btn--paused {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.5);
  color: #dc2626;
  opacity: 0.85;

  &:hover {
    background: rgba(239, 68, 68, 0.22);
    opacity: 1;
  }
}

:root.dark {
  .ai-toggle-btn--active {
    color: #4ade80;
  }
  .ai-toggle-btn--paused {
    color: #f87171;
  }
}

/* Densidade das seções de Ações (Participantes / Time / Etiquetas) no profile */
:deep(.conversation--actions .text-xs.font-medium.text-n-slate-11) {
  margin-bottom: 0.375rem;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}
</style>

